/**
 * Everything the browser must wire before the first render.
 *
 * Module scope, not an effect: a store or an API call issued during the first render must
 * not find null. Imported first by main.tsx.
 */
import { setStores } from '@/storage/projectStorePort';
import { configureApi } from './api/client';
import { accessToken, configureAuth, refreshAccessToken, signedInSub } from './api/auth';
import { projectsApi } from './api/projects';
import { createServerStores } from './platform/projectStore.server';

/*
 * Projects are kept on the server (/me/projects, O5), so an app built in one browser is edited
 * from any other; this browser's IndexedDB is the cache in front of it, and what projects built
 * before this are uploaded from on sign-in. See platform/projectStore.server.ts.
 */
// `signedInSub` is synchronous on purpose: the store asks it on every read and write (see auth.ts).
setStores(createServerStores({ api: projectsApi, identity: signedInSub }));

/*
 * Read from the same variables the bundle was built with. web/env.ts refuses a production
 * build without API_URL, BUILD_URL and the Cognito pair, so these are present in any real
 * release — the fallbacks are only for a dev server run without a full backend.
 *
 * buildUrl comes from the SAME variable the shared buildClient reads, so the old path and
 * the new typed client cannot disagree about where the build service is. Disagreeing was
 * the bug: BUILD_URL was missing from the bundle entirely and every build ended with
 * "this build needs a connection and has none".
 */
configureAuth({
  userPoolId: process.env.EXPO_PUBLIC_COGNITO_USER_POOL_ID ?? '',
  clientId: process.env.EXPO_PUBLIC_COGNITO_CLIENT_ID ?? '',
});

configureApi({
  apiUrl: process.env.EXPO_PUBLIC_API_URL ?? '',
  buildUrl: process.env.EXPO_PUBLIC_BUILD_URL ?? '',
  llmUrl: process.env.EXPO_PUBLIC_LLM_URL ?? '',
  // The ACCESS token: the API verifies token_use === 'access' and rejects an ID token with
  // a 401 even though the same pool signed it.
  getToken: accessToken,
  // One forced renewal when the server says 401, before anybody is told to sign in (F16).
  refreshToken: refreshAccessToken,
});
